import {
  createTheme,
  PaletteOptions,
  TypeText,
  PaletteColorOptions,
  MixinsOptions,
  TypographyVariantsOptions,
  TypographyStyle,
} from '@mui/material/styles';

const colors = {
  // Blue
  B050: '#DEEBFF',
  B075: '#B3D4FF',
  B100: '#4C9AFF',
  B200: '#2684FF',
  B300: '#0065FF',
  B400: '#0052CC',
  B500: '#0747A6',

  // Green
  G050: '#E2FFEE',
  G075: '#ABF5D1',
  G100: '#79F2C0',
  G200: '#57D9A3',
  G300: '#36B37E',
  G400: '#00875A',
  G500: '#006644',

  // Purple
  P050: '#EAE6FF',
  P075: '#C0B6F2',
  P100: '#998DD9',
  P200: '#8777D9',
  P300: '#6554C0',
  P400: '#5243AA',
  P500: '#403294',

  // Red
  R050: '#FFEBE5',
  R075: '#FFBDAD',
  R100: '#FF8F73',
  R200: '#FF7452',
  R300: '#FF5630',
  R400: '#DE350B',
  R500: '#BF2600',

  // Teal
  T050: '#E6FCFF',
  T075: '#B3F5FF',
  T100: '#79E2F2',
  T200: '#00C7E6',
  T300: '#00B8D9',
  T400: '#00A3BF',
  T500: '#008DA6',

  // Yellow
  Y050: '#FFFAE6',
  Y075: '#FFF0B3',
  Y100: '#FFE380',
  Y200: '#FFC400',
  Y300: '#FFAB00',
  Y400: '#FF991F',
  Y500: '#FF8B00',

  // Neutral
  N000: 'transparent',
  N010: '#FFFFFF',
  N020: '#F4F5F7',
  N030: '#EBECF0',
  N040: '#DFE1E6',
  N050: '#C1C7D0',
  N060: '#B3BAC5',
  N070: '#A5ADBA',
  N080: '#97A0AF',
  N090: '#8993A4',
  N100: '#7A869A',
  N200: '#6B778C',
  N300: '#5E6C84',
  N400: '#505F79',
  N500: '#42526E',
  N600: '#344563',
  N700: '#253858',
  N800: '#172B4D',
  N900: '#091E42',
};

export type CustomPalette = PaletteOptions & {
  purple: PaletteColorOptions;
  black: PaletteColorOptions;
  white: PaletteColorOptions;
  colors: typeof colors;
  text: Partial<TypeText & { hint: string }>;
};

const palette:CustomPalette = {
  black: {
    light: '#222222',
    main: '#222222',
    dark: '#222222',
  },
  white: {
    main: '#fff',
  },
  /**
   * material-ui default theme
   * oneOf(['light', 'dark])
   */
  mode: 'light',

  primary: {
    light: colors.B100,
    main: colors.B400,
    dark: colors.B500,
    50: '#DEEBFF',
    100: '#4C9AFF',
    200: '#2684FF',
    300: '#0065FF',
    400: '#0052CC',
    500: '#0747A6',
    contrastText: '#fff',
  },

  secondary: {
    light: '#ebecf0',
    main: '#c1c7d0',
    dark: '#A5ADBA',
    100: '#ebecf0',
    200: '#DADADA',
    300: '#A5ADBA',
    400: '#c1c7d0',
    500: '#A5ADBA',
    contrastText: '#fff',
  },

  error: {
    light: colors.R050,
    main: colors.R400,
    dark: colors.R500,
    50: '#FFEBE6',
    100: '#FF8F73',
    200: '#FF7452',
    300: '#FF5630',
    400: '#DE350B',
    500: '#BF2600',
    contrastText: '#fff',
  },

  warning: {
    light: colors.Y050,
    main: colors.Y400,
    dark: colors.Y500,
    50: '#FFFAE6',
    100: '#FFE380',
    200: '#FFC400',
    300: '#FFAB00',
    400: '#FF991F',
    500: '#FF8B00',
    contrastText: '#222222',
  },

  success: {
    light: colors.G075,
    main: colors.G400,
    dark: colors.G500,
    50: '#E3FCEF',
    100: '#79F2C0',
    200: '#57D9A3',
    300: '#36B37E',
    400: '#00875A',
    500: '#006644',
    contrastText: '#fff',
  },

  info: {
    light: '#E6FCFF',
    main: '#00B8D9',
    dark: '#008DA6',
    50: '#E6FCFF',
    100: '#79E2F2',
    200: '#00C7E6',
    300: '#00B8D9',
    400: '#00A3BF',
    500: '#008DA6',
    contrastText: '#222222',
  },

  purple: {
    light: colors.P050,
    main: colors.P300,
    dark: colors.P500,
    50: '#EAE6FF',
    100: '#998DD9',
    200: '#8777D9',
    300: '#6554C0',
    400: '#5243AA',
    500: '#403294',
    contrastText: '#fff',
  },

  text: {
    primary: '#000000',
    secondary: '#727273',
    disabled: colors.N200,
    hint: colors.N200,
  },

  // Deprecated
  grey: {
    100: '#fafbfc',
    200: '#F2F2F2',
    300: '#ebecf0',
    400: '#DADADA',
    500: '#c1c7d0',
  },

  colors,

  divider: '#DADADA',

  background: {
    paper: '#fff',
    default: '#fff',
  },

  action: {
    active: 'rgba(0, 0, 0, 0.54)',
    hover: '#F9F9F9',
    hoverOpacity: 0.04,
    selected: 'rgba(0, 0, 0, 0.08)',
    selectedOpacity: 0.08,
    disabled: 'rgba(0, 0, 0, 0.26)',
    disabledBackground: 'rgba(0, 0, 0, 0.12)',
    disabledOpacity: 0.38,
    focus: 'rgba(0, 0, 0, 0.12)',
    focusOpacity: 0.12,
    activatedOpacity: 0.12,
  },
};

declare module '@mui/material/SvgIcon' {
  interface SvgIconPropsSizeOverrides {}
  interface SvgIconPropsColorOverrides {
    black: true;
    white: true;
  }
}


type CustomTypographyOptions = TypographyVariantsOptions & {
  subtitle: TypographyStyle;
  body: TypographyStyle;
  number: TypographyStyle;
};


const baseFontFamily = '"Roboto", "Helvetica", "Arial", sans-serif';

const fontWeightLight = 300;
const fontWeightRegular = 400;
const fontWeightMedium = 500;
const fontWeightBold = 700;

const subtitle = {
  fontFamily: baseFontFamily,
  fontSize: 16,
  letterSpacing: 0,
  lineHeight: 'normal',
  fontWeight: fontWeightRegular,
};

const body = {
  fontFamily: baseFontFamily,
  fontSize: 14,
  letterSpacing: 0,
  lineHeight: 'normal',
  fontWeight: fontWeightRegular,
  cursor: 'default',
};

const typography: CustomTypographyOptions = {
  htmlFontSize: 14,
  fontFamily: baseFontFamily,
  fontSize: 14,
  fontWeightLight,
  fontWeightRegular,
  fontWeightMedium,
  fontWeightBold,

  /**
   * Header 1 level
   */
  h1: {
    fontFamily: baseFontFamily,
    fontSize: 20,
    letterSpacing: 0,
    lineHeight: 'normal',
    fontWeight: fontWeightMedium,
  },

  /**
   * Header 2 level
   */
  h2: {
    fontFamily: baseFontFamily,
    fontSize: 18,
    letterSpacing: 0,
    lineHeight: 'normal',
    fontWeight: fontWeightMedium,
  },

  /**
   * Header 3 level
   */
  h3: {
    fontFamily: baseFontFamily,
    fontSize: 16,
    letterSpacing: 0,
    lineHeight: 'normal',
    fontWeight: fontWeightRegular,
  },

  /**
   * Subtitles
   */
  subtitle,
  subtitle1: subtitle,
  subtitle2: subtitle,

  /**
   * Text
   */
  body,
  body1: body,
  body2: body,

  number: {
    fontFamily: baseFontFamily,
    fontSize: 12,
    letterSpacing: 0,
    lineHeight: 'normal',
    fontWeight: fontWeightRegular,
  },

  button: {
    fontFamily: baseFontFamily,
    fontSize: 14,
    letterSpacing: 0,
    lineHeight: 'normal',
    fontWeight: fontWeightRegular,
    textTransform: 'none',
  },

  /**
   * Table subheaders
   */
  caption: {
    fontFamily: baseFontFamily,
    fontSize: 12,
    letterSpacing: 0,
    lineHeight: 'normal',
    fontWeight: fontWeightRegular,
  },
};

const spacing = 4;

const trackColor = '#FAFAFA';

export interface CustomMixins extends MixinsOptions {
  scrollbars: Record<string, Record<string, any>>;
}

const mixins:CustomMixins = {
  scrollbars: {
    '&::-webkit-scrollbar': {
      backgroundColor: '#fff',
      width: 16,
      height: 16,
    },
  
    '&::-webkit-scrollbar-track': {
      backgroundColor: trackColor,
    },
  
    '&::-webkit-scrollbar-thumb': {
      border: `5px solid ${trackColor}`,
      borderRadius: 16,
      backgroundColor: '#C4C4C4',
      minHeight: 40,
      minWidth: 40,
  
      '&:hover': {
        backgroundColor: '#a0a0a5',
        border: `4px solid ${trackColor}`,
      },
    },
  
    '&::-webkit-scrollbar-button': {
      display: 'none',
    },
  },
};

export default createTheme({
  palette,
  spacing,
  shape: {
    borderRadius: 4,
  },
  shadows: [
    'none',
    '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)',
    '0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)',
    '0px 3px 3px -2px rgba(0,0,0,0.2),0px 3px 4px 0px rgba(0,0,0,0.14),0px 1px 8px 0px rgba(0,0,0,0.12)',
    '0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)',
    '0px 3px 5px -1px rgba(0,0,0,0.2),0px 5px 8px 0px rgba(0,0,0,0.14),0px 1px 14px 0px rgba(0,0,0,0.12)',
    '0px 3px 5px -1px rgba(0,0,0,0.2),0px 6px 10px 0px rgba(0,0,0,0.14),0px 1px 18px 0px rgba(0,0,0,0.12)',
    '0px 4px 5px -2px rgba(0,0,0,0.2),0px 7px 10px 1px rgba(0,0,0,0.14),0px 2px 16px 1px rgba(0,0,0,0.12)',
    '0px 5px 5px -3px rgba(0,0,0,0.2),0px 8px 10px 1px rgba(0,0,0,0.14),0px 3px 14px 2px rgba(0,0,0,0.12)',
    '0px 5px 6px -3px rgba(0,0,0,0.2),0px 9px 12px 1px rgba(0,0,0,0.14),0px 3px 16px 2px rgba(0,0,0,0.12)',
    '0px 6px 6px -3px rgba(0,0,0,0.2),0px 10px 14px 1px rgba(0,0,0,0.14),0px 4px 18px 3px rgba(0,0,0,0.12)',
    '0px 6px 7px -4px rgba(0,0,0,0.2),0px 11px 15px 1px rgba(0,0,0,0.14),0px 4px 20px 3px rgba(0,0,0,0.12)',
    '0px 7px 8px -4px rgba(0,0,0,0.2),0px 12px 17px 2px rgba(0,0,0,0.14),0px 5px 22px 4px rgba(0,0,0,0.12)',
    '0px 7px 8px -4px rgba(0,0,0,0.2),0px 13px 19px 2px rgba(0,0,0,0.14),0px 5px 24px 4px rgba(0,0,0,0.12)',
    '0px 7px 9px -4px rgba(0,0,0,0.2),0px 14px 21px 2px rgba(0,0,0,0.14),0px 5px 26px 4px rgba(0,0,0,0.12)',
    '0px 8px 9px -5px rgba(0,0,0,0.2),0px 15px 22px 2px rgba(0,0,0,0.14),0px 6px 28px 5px rgba(0,0,0,0.12)',
    '0px 8px 10px -5px rgba(0,0,0,0.2),0px 16px 24px 2px rgba(0,0,0,0.14),0px 6px 30px 5px rgba(0,0,0,0.12)',
    '0px 8px 11px -5px rgba(0,0,0,0.2),0px 17px 26px 2px rgba(0,0,0,0.14),0px 6px 32px 5px rgba(0,0,0,0.12)',
    '0px 9px 11px -5px rgba(0,0,0,0.2),0px 18px 28px 2px rgba(0,0,0,0.14),0px 7px 34px 6px rgba(0,0,0,0.12)',
    '0px 9px 12px -6px rgba(0,0,0,0.2),0px 19px 29px 2px rgba(0,0,0,0.14),0px 7px 36px 6px rgba(0,0,0,0.12)',
    '0px 10px 13px -6px rgba(0,0,0,0.2),0px 20px 31px 3px rgba(0,0,0,0.14),0px 8px 38px 7px rgba(0,0,0,0.12)',
    '0px 10px 13px -6px rgba(0,0,0,0.2),0px 21px 33px 3px rgba(0,0,0,0.14),0px 8px 40px 7px rgba(0,0,0,0.12)',
    '0px 10px 14px -6px rgba(0,0,0,0.2),0px 22px 35px 3px rgba(0,0,0,0.14),0px 8px 42px 7px rgba(0,0,0,0.12)',
    '0px 11px 14px -7px rgba(0,0,0,0.2),0px 23px 36px 3px rgba(0,0,0,0.14),0px 9px 44px 8px rgba(0,0,0,0.12)',
    '0px 11px 15px -7px rgba(0,0,0,0.2),0px 24px 38px 3px rgba(0,0,0,0.14),0px 9px 46px 8px rgba(0,0,0,0.12)',
  ],
  typography,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        // disable arrows on inputs
        '@global': {
          'input[type=number]::-webkit-inner-spin-button, input[type=number]::-webkit-outer-spin-button': {
            webkitAppearance: 'none',
            margin: 0,
          },
        },
      },
    },
    MuiSvgIcon: {
      variants: [
        {
          props: { fontSize: 'inherit' },
          style: { fontSize: 'inherit' }
        }
      ],
      styleOverrides: {
        root: {
          fontSize: spacing * 4.5,
        },
        fontSizeSmall: {
          fontSize: spacing * 3,
        },
        fontSizeLarge: {
          fontSize: spacing* 6,
        }
      }
    },
    MuiAutocomplete: {
      styleOverrides: {
        option: {
          lineHeight: '32px',
          height: '32px',
        },
      },
    },    
  },
  mixins,
});
